/**
 * CameraScriptApi — the `engine.camera` capability module (global service), entity-addressed.
 *
 * Lets scripts read/zoom a camera and switch the active camera, by entity. The backing is
 * injected per runtime (DI): orthographicSize get/set is backed by Camera2D component data
 * (+ a component-state version bump so the renderer re-reads); isActive/setActive reflect the
 * runtime's active-camera selection. `setActive` is OPTIONAL on the backing (no-ops where a
 * runtime has no switch hook), like PhysicsBacking's optional mutators.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle } from "../IEngineContext";

/** The injected backing the camera module resolves against (DI per runtime). */
export interface CameraBacking {
  getOrthographicSize(camera: EntityHandle): number;
  setOrthographicSize(camera: EntityHandle, size: number): void;
  isActive(camera: EntityHandle): boolean;
  /** Force this camera entity active. Optional — no-op where the runtime has no switch hook. */
  setActive?(camera: EntityHandle): void;
  /**
   * Screen/client coords → world through this camera's view (Unity's ScreenToWorldPoint).
   * Optional — the host implements it against its real viewport (canvas rect + camera
   * transform + ortho size). Returns null when the point is outside the game viewport.
   */
  screenToWorld?(camera: EntityHandle, screenX: number, screenY: number): { x: number; y: number } | null;
}

@scriptApi("camera", { tier: "safe", version: 1 })
export class CameraScriptApi {
  constructor(private readonly _backing: CameraBacking) {}

  @scriptFn() getOrthographicSize(camera: EntityHandle): number {
    return this._backing.getOrthographicSize(camera);
  }

  @scriptFn() setOrthographicSize(camera: EntityHandle, size: number): void {
    this._backing.setOrthographicSize(camera, size);
  }

  @scriptFn() isActive(camera: EntityHandle): boolean {
    return this._backing.isActive(camera);
  }

  @scriptFn() setActive(camera: EntityHandle): void {
    this._backing.setActive?.(camera);
  }

  /**
   * Convert screen/client coords (e.g. a `<Mouse>/position` action value) to world space
   * through this camera. Null when outside the game viewport or unsupported by the runtime.
   */
  @scriptFn() screenToWorld(camera: EntityHandle, screenX: number, screenY: number): { x: number; y: number } | null {
    return this._backing.screenToWorld?.(camera, screenX, screenY) ?? null;
  }
}
